import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Table } from '@react-apps/design-system';
import type { ColumnDef } from '@react-apps/design-system';
import type { Product } from '../../domain/entities/catalog.entity';
import { useProducts } from '../hooks/use-products';
import {
  CATALOG_PROVIDER_LABELS,
  PRODUCT_STATUS_BADGE,
  PRODUCT_STATUS_LABELS,
} from '../labels';
import { useProviderAccounts } from '@/modules/sales/presentation/hooks/use-provider-accounts';
import { CreateProductFromSourceModal } from './CreateProductFromSourceModal';
import { CreateProductModal } from './CreateProductModal';
import { Pagination } from '@react-apps/design-system';
import { RefreshButton } from '@/shared/RefreshButton';
import { usePageQueryParam } from '@/shared/hooks/use-page-query-param';

const PER_PAGE = 10;

export function ProductsPanel() {
  const navigate = useNavigate();
  const [page, setPage] = usePageQueryParam();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [sourceProduct, setSourceProduct] = useState<Product | null>(null);
  const { data, isLoading, refetch, isFetching } = useProducts(page, PER_PAGE);
  // A chave guarda o id da conta; quem lê a tabela reconhece o apelido dela.
  const { data: providerAccounts } = useProviderAccounts();
  const providerAccountNameById = useMemo(
    () =>
      new Map(
        (providerAccounts ?? []).map((account) => [
          account.id,
          account.displayName,
        ]),
      ),
    [providerAccounts],
  );

  const columns: ColumnDef<Product>[] = [
    { key: 'name', header: 'Nome' },
    {
      key: 'provider',
      header: 'Gateway',
      render: (row) =>
        row.provider ? (
          <div className="space-y-0.5">
            <p className="text-xs">{CATALOG_PROVIDER_LABELS[row.provider]}</p>
            {row.providerAccountId && (
              <p className="text-(--color-text-secondary) text-xs">
                {providerAccountNameById.get(row.providerAccountId) ??
                  row.providerAccountId}
              </p>
            )}
          </div>
        ) : (
          <span className="text-xs text-neutral-400">—</span>
        ),
    },
    {
      key: 'productExternalId',
      header: 'Produto × oferta',
      render: (row) =>
        row.productExternalId && row.offerExternalId ? (
          <span className="font-mono text-xs">
            {row.productExternalId}
            <span className="text-(--color-text-secondary)"> × </span>
            {row.offerExternalId}
          </span>
        ) : (
          <span className="text-xs text-neutral-400">—</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <Badge variant={PRODUCT_STATUS_BADGE[row.status]}>
          {PRODUCT_STATUS_LABELS[row.status]}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '1%',
      render: (row: Product) => (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="xs"
            className="whitespace-nowrap"
            onClick={(e) => {
              e.stopPropagation();
              setSourceProduct(row);
            }}
          >
            Criar novo baseado neste
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-(--color-text-secondary) text-sm">
          Produtos do catálogo. Clique em um produto para gerenciar status e
          perks associados.
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <RefreshButton
            onRefresh={() => void refetch()}
            isRefreshing={isFetching}
          />
          <Button onClick={() => setIsCreateOpen(true)}>Criar produto</Button>
        </div>
      </div>

      <CreateProductModal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
      />

      <CreateProductFromSourceModal
        source={sourceProduct}
        onClose={() => setSourceProduct(null)}
      />

      <Table
        columns={columns}
        data={data?.items ?? []}
        keyExtractor={(row) => row.id}
        loading={isLoading}
        emptyMessage="Nenhum produto encontrado."
        striped
        onRowClick={(row) =>
          void navigate(`/management/catalog/products/${row.id}`, {
            state: { product: row },
          })
        }
      />

      <Pagination
        page={page}
        total={data?.total ?? 0}
        perPage={PER_PAGE}
        loading={isLoading}
        label="produto"
        onPageChange={setPage}
      />
    </div>
  );
}
